// Vendor
import * as React from 'react';
import { inject } from 'mobx-react';
import { Link } from 'react-router-dom';

// Bernie
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClickTracker } from 'bernie-analytics';
import { ILocalization, LocalizedText } from 'bernie-l10n';
import { RouteStore } from 'bernie-core';

// UITK
import { UitkIcon } from 'uitk-react-icons';
import { UitkBadge, UitkBadgeTheme } from 'uitk-react-badge';
import { UitkCardLink } from 'uitk-react-cards';
import { UitkFigure, UitkImage } from 'uitk-react-images';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkSheet } from 'uitk-react-sheet';
import * as uitkReactUtils from 'uitk-react-utils';

// Components
import { FavoriteButton } from 'components/common/shortlist/favorite-button';
import { DisclaimerDialog } from 'components/common/disclaimer-dialog';
import { TargetableLink } from 'components/common/targetable-link';

const LinkTracked = ClickTracker(Link);

export interface MapSheetProps {
  sheet: any;
  selectedHotel: any;
  onClick: () => void;
  onFavoriteClick: () => void;
  isVisible?: boolean;
  l10n: ILocalization;
}

export const MapSheet: React.FunctionComponent<MapSheetProps> = ({ sheet, selectedHotel, onClick, onFavoriteClick, isVisible, l10n }) => {
  if (!sheet) {
    return null;
  }

  const containerClasses = uitkReactUtils.classNames(
    'uitk-cell',
    'all-cell-fill',
    'all-cell-1-2',
    'all-l-padding-three',
    'all-b-padding-three'
  );

  const isPOI =
    selectedHotel &&
    Object.keys(selectedHotel).length === 2 &&
    Object.keys(selectedHotel).includes('id') &&
    Object.keys(selectedHotel).includes('title');
  const rfrrHeart = 'ModuleMapPinnedPropertyHeart';
  const rfrrHotelLink = 'ModuleMapPinnedPropertyLink';
  const hotelLinkProps = {
    onClick,
    rfrr: rfrrHotelLink,
    to: sheet.linkOff || ''
  };

  return (
    <UitkSheet className="uitk-grid hsr-map-sheet" isVisible={isVisible}>
      <div className="uitk-cell all-cell-1-4">
        <Thumbnail linkOff={sheet.linkOff} onClick={onClick} photoUrl={sheet.photoUrl} rfrrHotelLink={rfrrHotelLink} />
      </div>
      <div className={containerClasses}>
        <SheetContent
          sheet={sheet}
          isPOI={isPOI}
          hotel={selectedHotel}
          l10n={l10n}
          onFavoriteClick={onFavoriteClick}
          rfrrHeart={rfrrHeart}
        />
      </div>
      {!isPOI && (
        <TargetableLink>
          <HotelLink {...hotelLinkProps} />
        </TargetableLink>
      )}
    </UitkSheet>
  );
};

const HotelLink = props => (
  <UitkCardLink>
    <LinkTracked linkName="View hotel" {...props} />
  </UitkCardLink>
);

interface RatingProps {
  subtitle: string;
}

const Rating = ({ subtitle }: RatingProps) => (
  <div className="uitk-cell all-cell-1-1 uitk-type-200__medium map-viewer__info-sheet-reviews">
    <span className="map-viewer__info-sheet-reviews--rating">{subtitle}</span>
  </div>
);

interface ThumbnailProps {
  linkOff?: string;
  onClick: () => void;
  photoUrl?: string;
  rfrrHotelLink?: string;
}

const Thumbnail = ({ linkOff, onClick, photoUrl, rfrrHotelLink }: ThumbnailProps) =>
  photoUrl ? (
    <LinkTracked linkName="View hotel" onClick={onClick} rfrr={rfrrHotelLink} to={linkOff || ''}>
      <UitkFigure className="all-image-fit-cover" src={photoUrl} />
    </LinkTracked>
  ) : (
    <UitkFigure className="all-image-fit-cover">
      <UitkImage placeholderImage={true} alt="" />
    </UitkFigure>
  );

interface FaveButtonProps {
  hotel: any;
  l10n?: any;
  onClick?: () => void;
  rfrrHeart?: string;
  sheet?: any;
}

export const FaveButton = ({ hotel, l10n, onClick, rfrrHeart, sheet }: FaveButtonProps) => {
  const { hotelId, shortlistModel } = hotel;

  if (!hotelId || !shortlistModel) {
    return null;
  }

  return (
    <FavoriteButton
      category="hotel"
      shortlistModel={shortlistModel}
      id={`${hotelId}`}
      key={hotelId}
      labelRemove={l10n.formatText('hotels.searchResults.shortlist.removeFromList.a11yLabel', name)}
      labelSave={l10n.formatText('hotels.searchResults.shortlist.saveToList.a11yLabel', sheet.name)}
      onClick={onClick}
      onSaveMessage={l10n.formatText('hotels.searchResults.shortlist.saveToList.notification', name)}
      referralObject={{
        favorite: {
          rfrr: `${rfrrHeart}.SaveFavorite`
        },
        unfavorite: {
          rfrr: `${rfrrHeart}.RemoveFavorite`
        }
      }}
      rfrr={rfrrHeart}
    />
  );
};

interface PriceBlockProps {
  hotelId?: number;
  l10n?: any;
  routeStore?: RouteStore;
  sheet: {
    badgeText?: string;
    badgeTheme?: UitkBadgeTheme;
    leadPrice?: string;
    strikeoutDisclaimer?: string;
    strikeoutPrice?: string;
  };
}

const PriceBlock: React.FunctionComponent<PriceBlockProps> = ({ hotelId, l10n, routeStore, sheet }) => {
  const { badgeText, badgeTheme, leadPrice: lead, strikeoutPrice: strikeout, strikeoutDisclaimer } = sheet;

  if (!lead) {
    return (
      <div className="uitk-cell uitk-type-400 map-viewer__info-sheet-price map-viewer__info-sheet-price--soldout">
        <LocalizedText message="maps.sheets.hotel.soldOut.text" />
      </div>
    );
  }

  const dialogId = hotelId ? `priceDisclaimerDialogMap-${hotelId}` : 'priceDisclaimerDialogMap';
  const dialogQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialogNested: dialogId });
  const routeStoreGoBack = () => {
    routeStore.goBack();
  };

  return (
    <div className="uitk-cell all-cell-1-1 uitk-type-right all-t-padding-two all-r-padding-three">
      {badgeText && badgeTheme && <UitkBadge text={badgeText} theme={UitkBadgeTheme[badgeTheme]} />}
      <div className="uitk-grid all-grid-align-end all-t-padding-half all-cell-1-2 map-viewer__info-sheet-price">
        {strikeout && (
          <React.Fragment>
            {strikeoutDisclaimer ? (
              <React.Fragment>
                <UitkMoreInfoTrigger
                  className="uitk-cell all-cell-fill all-cell-align-end uitk-type-300 uitk-type-medium
                  all-x-padding-one all-t-padding-half map-viewer__info-sheet-price--strikethrough"
                >
                  <LinkTracked
                    linkName="Price Disclaimer Dialog"
                    rfrr=".Price.DisclaimerDialog.Map"
                    to={{ pathname: routeStore.pathname, search: dialogQuery }}
                  >
                    <UitkIcon name="info_outline" className="pwa-theme--grey-700 all-r-padding-half" />
                    <del>
                      <span className="is-visually-hidden map-viewer__info-sheet-price--strikethrough--a11y">
                        {l10n.formatText('hotels.infosite.rateInfoDialog.strikeOut', strikeout)}
                      </span>
                      <span className="map-viewer__info-sheet-price--strikethrough--text pwa-theme--grey-700" aria-hidden="true">
                        {strikeout}
                      </span>
                    </del>
                  </LinkTracked>
                </UitkMoreInfoTrigger>
                <DisclaimerDialog
                  dialogId={dialogId}
                  dialogType="pwaDialogNested"
                  disclaimerText={strikeoutDisclaimer}
                  handleClose={routeStoreGoBack}
                  innerHTML={true}
                  layerId="price-disclaimer-dialog-map"
                  disclaimerTextId="price-disclaimer-text"
                />
              </React.Fragment>
            ) : (
              <del
                className="uitk-cell all-cell-fill uitk-type-right uitk-type-300 uitk-type-medium all-x-padding-one
                all-t-padding-half map-viewer__info-sheet-price--strikethrough"
              >
                <span className="is-visually-hidden map-viewer__info-sheet-price--strikethrough--a11y">
                  {l10n.formatText('hotels.infosite.rateInfoDialog.strikeOut', strikeout)}
                </span>
                <span className="map-viewer__info-sheet-price--strikethrough--text pwa-theme--grey-700" aria-hidden="true">
                  {strikeout}
                </span>
              </del>
            )}
          </React.Fragment>
        )}
        {lead && <div className="uitk-cell all-cell-shrink all-cell-1-2 uitk-type-bold uitk-type-600">{lead}</div>}
      </div>
    </div>
  );
};

interface SheetContentProps {
  hotel?: any;
  isPOI?: boolean;
  l10n?: any;
  onFavoriteClick?: () => void;
  rfrrHeart?: string;
  sheet: any;
}

const SheetContent = ({ hotel, isPOI, l10n, onFavoriteClick, rfrrHeart, sheet }: SheetContentProps) => {
  const { distanceFromPOI, subtitle, title, vipMessage } = sheet;
  const hotelId = hotel && hotel.hotelId;
  const hasFaveButton = hotelId && hotel.shortlistModel;
  const rightPadding = hasFaveButton ? '' : 'all-r-padding-three';

  return (
    <div className="uitk-grid">
      <div className="uitk-grid">
        <div className={`uitk-cell all-cell-fill all-t-padding-three ${rightPadding}`} data-stid="hsr-map-sheet-content">
          {vipMessage && <div className="uitk-type-200 uitk-type-medium map-vip">{vipMessage}</div>}
          <div className="uitk-type-500 uitk-type-bold truncate map-viewer__info-sheet-name">{title}</div>
          {distanceFromPOI && <PoiInfo distanceFromPOI={distanceFromPOI} />}
          {subtitle && <Rating subtitle={subtitle} />}
        </div>
        {hotel && <FaveButton hotel={hotel} l10n={l10n} onClick={onFavoriteClick} rfrrHeart={rfrrHeart} sheet={sheet} />}
      </div>
      {!isPOI && <Price sheet={sheet} l10n={l10n} hotelId={hotelId} />}
    </div>
  );
};

interface PoiInfoProps {
  distanceFromPOI: string;
}

const PoiInfo = ({ distanceFromPOI }: PoiInfoProps) => (
  <AbacusExperiment name="PWA_HSR_Map_Add_POI_Marker">
    <AbacusControl />
    <AbacusVariant bucket={1}>
      <div className="uitk-cell all-cell-1-1 uitk-type-200__medium pwa-theme--grey-700 map-viewer__info-sheet-poi">{distanceFromPOI}</div>
    </AbacusVariant>
  </AbacusExperiment>
);

export const Price = inject('routeStore')(PriceBlock);
